import { call, put, takeLatest } from '@redux-saga/core/effects'
import { select } from 'redux-saga/effects'
import { getSelectedItem } from '../selectors'
import { IItem, ITimelineState } from '../store/timeline.types'
import { TimelineConstants } from '../store/timeline.constants'
import { getPcrIdSelector } from '../../home/selectors'
import { IPcr } from '../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../user/selectors'
import { TimesConstants } from '../../times/store/times.constants'
import {
  setActivityLog,
  setDeleteParentVitals,
  setDeleteProcedure,
  setDeleteProcedurePrompt,
  setDeleteVitalsPrompt,
} from '../store/timeline.actions'
import { getTimes } from '../../times/store/times.actions'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { modifyParentVitalsObject } from '../util/modifyParentVitalsObject.util'
import { getPatientTimelineSelector } from '../selectors/patientTimeline.selector'
import { deleteAssociatedProcedure } from '../util/deleteAssociatedProcedure.util'
import { updateCustomGroupsFromTimeLine } from '../../nemsisData/store/nemsisCustomData.actions'
import { StateCustomDataConstants } from '../../nemsisData/store/nemsisCustomData.constants'
import { isValidVitalData } from '../../nemsisData/utils/customElements.util'
import { resetState as resetIntubationState } from '../../intubation/store/intubation.actions'
import { resetState as resetCardiacProcedureState } from '../../procedures/cardiac/store/cardiac.actions'
import { resetState as resetAirwayProcedureState } from '../../procedures/airway/store/airway.actions'
import { resetState as resetChildbirthOBProcedureState } from '../../procedures/childbirthOB/store/childbirthOB.actions'
import { resetState as resetInitiateIvProcedureState } from '../../procedures/initiateIv/store/initiateIv.actions'
import { resetState as resetImmobilizationProcedureState } from '../../procedures/immobilization/store/immobilization.actions'
import { resetState as resetMedicalConsultProcedureState } from '../../procedures/medicalConsult/store/medicalConsult.actions'
import { resetState as resetExtricationProcedureState } from '../../procedures/extrication/store/extrication.actions'
import { resetState as resetOperationsProcedureState } from '../../procedures/operations/store/operations.actions'
import { resetState as resetRescueProcedureState } from '../../procedures/rescue/store/rescue.actions'
import { resetState as resetHospitalNotifyProcedureState } from '../../procedures/hospitalNotify/store/hospitalNotify.actions'
import { resetState as resetWoundCareProcedureState } from '../../procedures/woundCare/store/woundCare.actions'
import { resetState as resetVentilatorProcedureState } from '../../procedures/ventilator/store/ventilator.actions'
import { resetState as resetDrainProcedureState } from '../../procedures/drain/store/drain.actions'
import { resetState as resetImagingUltrasoundProcedureState } from '../../procedures/imagingUltrasound/store/ImagingUltrasound.actions'
import { resetState as resetPnProcedureState } from '../../procedures/pnProcedure/store/pnProcedure.actions'
import { resetLabsState as resetLabsProcedureState } from '../../procedures/labs/store/labs.actions'
import { resetTitrateState as resetTitrateProcedureState } from '../../procedures/titrate/store/titrate.actions'
import { resetPnMedicationState as resetPnMedicationProcedureState } from '../../procedures/pnMedication/store/pnMedication.actions'
import { resetState as resetBloodProductAdministrationProcedureState } from '../../procedures/bloodProductAdministration/store/bloodProductAdministration.actions'
import { resetState as resetAssessmentProcedureState } from '../../procedures/assessment/store/assessment.actions'
import { resetState as resetVentricularAssistDeviceProcedureState } from '../../procedures/ventricularAssistDevice/store/ventricularAssistDevice.actions'
import { resetState as resetCriticalCareProcedureState } from '../../procedures/criticalCare/store/criticalCare.actions'

export function* deleteTimelineItem() {
  const selectedItem: IItem = yield select(getSelectedItem)
  const { deleteParentVitals, deleteProcedure }: ITimelineState = yield select(getPatientTimelineSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  const serviceId: string = yield select(getServiceIdSelector)
  if (pcrID) {
    try {
      if (selectedItem) {
        const pcrDocsService = new PcrDocsService(serviceId)
        let pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
        const currentVital = pcrDoc.patient?.patientVitals?.find((vital) => vital?.key === selectedItem?.key)
        const isValidData = currentVital && isValidVitalData(currentVital)

        // Handle parent vitals from EKG import with child procedures
        if (
          selectedItem.clientEventId &&
          selectedItem.vsAction &&
          selectedItem.rowActionType === TimelineConstants.SET_VITAL_ITEM &&
          !deleteParentVitals
        ) {
          yield put(setDeleteVitalsPrompt(true))
          return
        }
        if (deleteParentVitals) {
          yield put(setDeleteParentVitals(false))
        }

        if (deleteProcedure) {
          yield put(setDeleteProcedure(false))
        }

        const isVitalItem = selectedItem.rowActionType === TimelineConstants.SET_VITAL_ITEM

        if (
          (!isVitalItem && !deleteProcedure) ||
          (isVitalItem && isValidData && !selectedItem.vsAction && !selectedItem.clientEventId && !deleteProcedure) ||
          (isVitalItem && isValidData && selectedItem.isEkgImport && !deleteProcedure)
        ) {
          yield put(setDeleteProcedurePrompt(true))
          return
        }

        // Main delete functionality
        const itemIndex = pcrDoc.patient.timelineItems?.findIndex((item) => item.key === selectedItem.key)
        if (itemIndex !== undefined && itemIndex !== -1) {
          pcrDoc.patient.timelineItems?.splice(itemIndex, 1)
        }

        switch (selectedItem.rowActionType) {
          case TimelineConstants.SET_VITAL_ITEM:
            if (pcrDoc.patient?.patientVitals && pcrDoc.patient?.patientVitals.length) {
              const index = pcrDoc.patient?.patientVitals?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.patientVitals.splice(index, 1)

                if (selectedItem.vsAction && selectedItem.clientEventId) {
                  pcrDoc = deleteAssociatedProcedure(selectedItem, pcrDoc)
                }
              }
            }
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_INTUBATION_PROCEDURE_ITEM:
            if (pcrDoc.patient?.intubationStateList && pcrDoc.patient?.intubationStateList.length) {
              const index = pcrDoc.patient?.intubationStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.intubationStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetIntubationState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_CARDIAC_PROCEDURE_ITEM:
            if (pcrDoc.patient?.cardiacStateList && pcrDoc.patient?.cardiacStateList.length) {
              const index = pcrDoc.patient?.cardiacStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.cardiacStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetCardiacProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_AIRWAY_PROCEDURE_ITEM:
            if (pcrDoc.patient?.airwayStateList && pcrDoc.patient?.airwayStateList.length) {
              const index = pcrDoc.patient?.airwayStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.airwayStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetAirwayProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_CHILDBIRTHOB_PROCEDURE_ITEM:
            if (pcrDoc.patient?.childbirthOBStateList && pcrDoc.patient?.childbirthOBStateList.length) {
              const index = pcrDoc.patient?.childbirthOBStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.childbirthOBStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetChildbirthOBProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_INITIATE_IV_PROCEDURE_ITEM:
            if (pcrDoc.patient?.initiateIvStateList && pcrDoc.patient?.initiateIvStateList.length) {
              const index = pcrDoc.patient?.initiateIvStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.initiateIvStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetInitiateIvProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_IMMOBILIZATION_PROCEDURE_ITEM:
            if (pcrDoc.patient?.immobilizationStateList && pcrDoc.patient?.immobilizationStateList.length) {
              const index = pcrDoc.patient?.immobilizationStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.immobilizationStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetImmobilizationProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_MEDICAL_CONSULT_PROCEDURE_ITEM:
            if (pcrDoc.patient?.medicalConsultStateList && pcrDoc.patient?.medicalConsultStateList.length) {
              const index = pcrDoc.patient?.medicalConsultStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.medicalConsultStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetMedicalConsultProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_EXTRICATION_PROCEDURE_ITEM:
            if (pcrDoc.patient?.extricationStateList && pcrDoc.patient?.extricationStateList.length) {
              const index = pcrDoc.patient?.extricationStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.extricationStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetExtricationProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_OPERATIONS_PROCEDURE_ITEM:
            if (pcrDoc.patient?.operationsStateList && pcrDoc.patient?.operationsStateList.length) {
              const index = pcrDoc.patient?.operationsStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.operationsStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetOperationsProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_RESCUE_PROCEDURE_ITEM:
            if (pcrDoc.patient?.rescueStateList && pcrDoc.patient?.rescueStateList.length) {
              const index = pcrDoc.patient?.rescueStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.rescueStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetRescueProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_HOSPITAL_NOTIFY_PROCEDURE_ITEM:
            if (pcrDoc.patient?.hospitalNotifyStateList && pcrDoc.patient?.hospitalNotifyStateList.length) {
              const index = pcrDoc.patient?.hospitalNotifyStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.hospitalNotifyStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetHospitalNotifyProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_WOUND_CARE_PROCEDURE_ITEM:
            if (pcrDoc.patient?.woundCareStateList && pcrDoc.patient?.woundCareStateList.length) {
              const index = pcrDoc.patient?.woundCareStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.woundCareStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetWoundCareProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_VENTILATOR_PROCEDURE_ITEM:
            if (pcrDoc.patient?.ventilatorStateList && pcrDoc.patient?.ventilatorStateList.length) {
              const index = pcrDoc.patient?.ventilatorStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.ventilatorStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetVentilatorProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_DRAIN_PROCEDURE_ITEM:
            if (pcrDoc.patient?.drainStateList && pcrDoc.patient?.drainStateList.length) {
              const index = pcrDoc.patient?.drainStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.drainStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetDrainProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_ADD_MEDICATION_ADMINISTERED_ITEM:
            if (
              pcrDoc.patient?.medicationAdministeredStateList &&
              pcrDoc.patient?.medicationAdministeredStateList.length
            ) {
              const index = pcrDoc.patient?.medicationAdministeredStateList?.findIndex(
                (mas) => mas.key === selectedItem.key
              )
              if (index !== -1) {
                pcrDoc.patient.medicationAdministeredStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            yield put(setDeleteProcedurePrompt(false))
            return
          case TimelineConstants.SET_IMAGING_ULTRASOUND_ITEM:
            if (pcrDoc.patient?.imagingUltrasoundStateList && pcrDoc.patient?.imagingUltrasoundStateList.length) {
              const index = pcrDoc.patient?.imagingUltrasoundStateList?.findIndex((mas) => mas.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.imagingUltrasoundStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetImagingUltrasoundProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_TITRATE_PROCEDURE_ITEM:
            if (pcrDoc.patient?.titrateStateList && pcrDoc.patient?.titrateStateList.length) {
              const index = pcrDoc.patient?.titrateStateList?.findIndex((ts) => ts.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.titrateStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetTitrateProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_LABS_PROCEDURE_ITEM:
            if (pcrDoc.patient?.labsStateList && pcrDoc.patient?.labsStateList.length) {
              const index = pcrDoc.patient?.labsStateList?.findIndex((lab) => lab.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.labsStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetLabsProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_PN_PROCEDURE_ITEM:
            if (pcrDoc.patient?.procedureTypeStateListPNNVs && pcrDoc.patient?.procedureTypeStateListPNNVs.length) {
              const index = pcrDoc.patient?.procedureTypeStateListPNNVs?.findIndex((ts) => ts.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.procedureTypeStateListPNNVs.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetPnProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            return
          case TimelineConstants.SET_PN_MEDICATION_ITEM:
            if (
              pcrDoc.patient?.medicationAdministeredStateListPNNVs &&
              pcrDoc.patient?.medicationAdministeredStateListPNNVs.length
            ) {
              const index = pcrDoc.patient?.medicationAdministeredStateListPNNVs?.findIndex(
                (ts) => ts.key === selectedItem.key
              )
              if (index !== -1) {
                pcrDoc.patient.medicationAdministeredStateListPNNVs.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetPnMedicationProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            return
          case TimelineConstants.SET_BLOOD_PRODUCT_ADMINISTRATION:
            if (
              pcrDoc.patient?.bloodProductAdministrationStateList &&
              pcrDoc.patient?.bloodProductAdministrationStateList.length
            ) {
              const index = pcrDoc.patient?.bloodProductAdministrationStateList?.findIndex(
                (bloodProduct) => bloodProduct.key === selectedItem.key
              )
              if (index !== -1) {
                pcrDoc.patient.bloodProductAdministrationStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetBloodProductAdministrationProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_ASSESSMENT:
            if (pcrDoc.patient?.assessmentsStateList && pcrDoc.patient?.assessmentsStateList.length) {
              const index = pcrDoc.patient?.assessmentsStateList?.findIndex((data) => data.key === selectedItem.key)
              if (index !== -1) {
                pcrDoc.patient.assessmentsStateList.splice(index, 1)
              }
            }
            yield put(resetAssessmentProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_VENTRICULAR_ASSIST_DEVICE:
            if (
              pcrDoc.patient?.ventricularAssistDeviceStateList &&
              pcrDoc.patient?.ventricularAssistDeviceStateList.length
            ) {
              const index = pcrDoc.patient?.ventricularAssistDeviceStateList?.findIndex(
                (data) => data.key === selectedItem.key
              )
              if (index !== -1) {
                pcrDoc.patient.ventricularAssistDeviceStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetVentricularAssistDeviceProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_CRITICAL_CARE_PROCEDURE_ITEM:
            if (pcrDoc.patient?.criticalCareStateList && pcrDoc.patient?.criticalCareStateList.length) {
              const index = pcrDoc.patient?.criticalCareStateList?.findIndex(
                (criticalCareObj) => criticalCareObj.key === selectedItem.key
              )
              if (index !== -1) {
                pcrDoc.patient.criticalCareStateList.splice(index, 1)

                const isEkgImport = selectedItem.isEkgImport === 1 || selectedItem.isEkgImport === -1
                if (isEkgImport) pcrDoc = modifyParentVitalsObject(selectedItem, pcrDoc)
              }
            }
            yield put(resetCriticalCareProcedureState())
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.REMOVE_TIMELINE_GROUP))
            return
          case TimelineConstants.SET_TIMES_ITEM:
            if (pcrDoc.times) {
              if (selectedItem.key === TimesConstants.ADD_ONSET_CURRENT_DATETIME) {
                pcrDoc.times.onsetTime = ''
                pcrDoc.times.onsetDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_RECEIVED_CURRENT_DATETIME) {
                pcrDoc.times.receivedTime = ''
                pcrDoc.times.receivedDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_DISPATCH_NOTIFIED_CURRENT_DATETIME) {
                pcrDoc.times.dispatchNotifiedTime = ''
                pcrDoc.times.dispatchNotifiedDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_DISPATCHED_CURRENT_DATETIME) {
                pcrDoc.times.dispatchedTime = ''
                pcrDoc.times.dispatchedDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_ACKNOWLEDGED_CURRENT_DATETIME) {
                pcrDoc.times.acknowledgedTime = ''
                pcrDoc.times.acknowledgedDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_EN_ROUTE_CURRENT_DATETIME) {
                pcrDoc.times.enRouteTime = ''
                pcrDoc.times.enRouteDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_STAGING_CURRENT_DATETIME) {
                pcrDoc.times.stagingTime = ''
                pcrDoc.times.stagingDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_ON_SCENE_CURRENT_DATETIME) {
                pcrDoc.times.onSceneTime = ''
                pcrDoc.times.onSceneDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_AT_PATIENT_CURRENT_DATETIME) {
                pcrDoc.times.atPatientTime = ''
                pcrDoc.times.atPatientDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_TRANSFER_CARE_EMS_CURRENT_DATETIME) {
                pcrDoc.times.transferCareEMSTime = ''
                pcrDoc.times.transferCareEMSDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_LEAVE_REFERRING_CURRENT_DATETIME) {
                pcrDoc.times.leaveReferringTime = ''
                pcrDoc.times.leaveReferringDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_ARRIVE_RECEIVING_CURRENT_DATETIME) {
                pcrDoc.times.arriveReceivingTime = ''
                pcrDoc.times.arriveReceivingDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_TRANSFER_CARE_EMS_CURRENT_DATETIME) {
                pcrDoc.times.transferCareEMSTime = ''
                pcrDoc.times.transferCareEMSDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_AVAILABLE_CURRENT_DATETIME) {
                pcrDoc.times.availableTime = ''
                pcrDoc.times.availableDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_DESCENT_CURRENT_DATETIME) {
                pcrDoc.times.descentTime = ''
                pcrDoc.times.descentDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_AT_LANDING_CURRENT_DATETIME) {
                pcrDoc.times.atLandingTime = ''
                pcrDoc.times.atLandingDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_CALL_COMPLETED_CURRENT_DATETIME) {
                pcrDoc.times.callCompletedTime = ''
                pcrDoc.times.callCompletedDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_IN_QUARTERS_CURRENT_DATETIME) {
                pcrDoc.times.inQuartersTime = ''
                pcrDoc.times.inQuartersDate = ''
              }
              if (selectedItem.key === TimesConstants.ADD_CANCELLED_CURRENT_DATETIME) {
                pcrDoc.times.cancelledTime = ''
                pcrDoc.times.cancelledDate = ''
              }
              if (selectedItem.key === TimesConstants.SET_CUSTOM_1_DATETIME) {
                pcrDoc.times.custom1Time = ''
                pcrDoc.times.custom1Date = ''
              }
              if (selectedItem.key === TimesConstants.SET_CUSTOM_2_DATETIME) {
                pcrDoc.times.custom2Time = ''
                pcrDoc.times.custom2Date = ''
              }
              if (selectedItem.key === TimesConstants.SET_CUSTOM_3_DATETIME) {
                pcrDoc.times.custom3Time = ''
                pcrDoc.times.custom3Date = ''
              }
              if (selectedItem.key === TimesConstants.SET_CUSTOM_4_DATETIME) {
                pcrDoc.times.custom4Time = ''
                pcrDoc.times.custom4Date = ''
              }
            }
            yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
            yield put(setActivityLog(pcrDoc.patient.timelineItems))
            yield put(getTimes())
            return
        }
      }
    } catch (e) {
      yield put(throwPcrException(`deleteTimelineItem() error: ${e}`))
    }
  } else {
    // todo: add to audit log
  }
}

export function* deleteTimelineItemSaga() {
  yield takeLatest([TimelineConstants.DELETE_TIMELINE_ITEM], performWithMutexHOG, deleteTimelineItem)
}
